import { CiCircleRemove, CiInboxIn, CiRedo, CiSearch } from "react-icons/ci";
import PageTitle from "../../components/common/PageTitle";
import { Navigate, NavLink, useLocation, useNavigate } from "react-router-dom";
import ButtonTextIcon from "../../components/ui/ButtonTextIcon";
import { decodeRolesFromToken, formatDateTime } from "../../utilities/helpers";
import { useCallback, useEffect, useState } from "react";
import { API_URL, APP_CURRENCY } from "../../utilities/constants";
import { useUI } from "../../context/ui";
import Pagination from "../../components/common/Pagination";
import FullPageLoader from "../../components/ui/FullPageLoader";
import ResultsPerPage from "../_common/ResultsPerPage";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { AiOutlineCheckCircle } from "react-icons/ai";
import RSModal from "../../components/ui/RSModal";
import LoaderIcon from "../../components/ui/LoaderIcon";
import { FaArrowLeftLong } from "react-icons/fa6";
import ButtonLightTextIcon from "../../components/ui/ButtonLightTextIcon";
import { IoIosMegaphone } from "react-icons/io";
import NotificationPopup from "./CustomerNotif/NotifPopUp";

const buildQueryParams = (params) =>
  Object.entries(params)
    .filter(([_, value]) => value !== "")
    .map(([key, value]) => `${key}=${value}`)
    .join("&");

const CustomerList = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    setSegmentTitle,
    setNewNotification,
    setShowNotification,
    addNotification,
    addToastMessage,
  } = useUI();
  const userRole = decodeRolesFromToken()[0];
  const pageNo = new URLSearchParams(location.search).get("pageNo") || 0;
  const pageSize = new URLSearchParams(location.search).get("pageSize") || 10;
  const filterType =
    new URLSearchParams(location.search).get("filterType") || "";
  const search = new URLSearchParams(location.search).get("search") || "";
  const startDate = new URLSearchParams(location.search).get("startDate") || "";
  const endDate = new URLSearchParams(location.search).get("endDate") || "";
  const statusID = new URLSearchParams(location.search).get("statusID") || "";
  const minBalance =
    new URLSearchParams(location.search).get("minBalance") || "";
  const maxBalance =
    new URLSearchParams(location.search).get("maxBalance") || "";
  const [initialValues, setInitialValues] = useState({
    filterType: filterType || "",
    search:
      filterType === "balance" ? "" : filterType === "date" ? "" : search || "",
    minBalance: filterType === "balance" ? parseFloat(minBalance) || "" : "",
    maxBalance: filterType === "balance" ? parseFloat(maxBalance) || "" : "",
    startDate: filterType === "startDate" ? new Date(startDate) : "",
    endDate: filterType === "endDate" ? new Date(endDate) : "",
    statusID: statusID,
  });

  const [excludedTxnIds, setExcludedTxnIds] = useState([]);
  const [includedTxnIds, setIncludedTxnIds] = useState([]);
  const [allChecked, setAllChecked] = useState(false);

  const resetFilters = () => {
    const queryParams = buildQueryParams({
      filterType: "",
      searchType: "",
      search: "",
      pageNo: 0,
      pageSize,
      minBalance: "",
      maxBalance: "",
      startDate: "",
      endDate: "",
      statusID: "",
    });
    setAllChecked(false);
    setIncludedTxnIds([]);
    setExcludedTxnIds([]);
    navigate(`/customers?${queryParams}`);
  };

  const callListApi = async () => {
    setLoading(true);

    let apiUrl = `${API_URL}/api/1.0.0/user?roleID=5`;

    const searchType =
      filterType === "name"
        ? 1
        : filterType === "email"
        ? 2
        : filterType === "accountNumber"
        ? 3
        : filterType === "balance"
        ? 4
        : filterType === "date"
        ? 5
        : filterType === "status"
        ? 6
        : 0;

    if (filterType === "balance") {
      apiUrl += `&searchType=${searchType}&minBalance=${minBalance}&maxBalance=${maxBalance}&pageNo=${pageNo}&pageSize=${pageSize}`;
    } else if (filterType === "date") {
      apiUrl += `&searchType=${searchType}&startDate=${startDate}&endDate=${endDate}&pageNo=${pageNo}&pageSize=${pageSize}`;
    } else if (filterType === "status") {
      apiUrl += `&searchType=${searchType}&statusID=${statusID}&pageNo=${pageNo}&pageSize=${pageSize}`;
    } else if (filterType) {
      apiUrl += `&searchType=${searchType}&search=${search}&pageNo=${pageNo}&pageSize=${pageSize}`;
    } else {
      apiUrl += `&pageNo=${pageNo}&pageSize=${pageSize}`;
    }

    try {
      const resp = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-JWT-Assertion": localStorage.getItem("token"),
        },
      });

      if (resp.ok) {
        const response = await resp.json();
        setData(response.data);
      } else {
        console.error("Failed to fetch data");
      }
    } catch (e) {
      console.error("Error", e);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (id, status, setShow) => {
    try {
      const updatedStatus = status === 1 ? 2 : 1;

      setLoading(true);

      const response = await fetch(`${API_URL}/api/1.0.0/user/${id}/status`, {
        method: "PUT",
        headers: {
          "X-JWT-Assertion": localStorage.getItem("token"),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ statusID: updatedStatus }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage =
          errorData.responseDescription ||
          "Something went wrong. Please try again!";
        addToastMessage({
          status: false,
          message: errorMessage,
        });
        return;
      }

      addToastMessage({
        status: true,
        message: "You have successfully updated the user status!",
      });
      setNewNotification(true);
      setShowNotification(false);
      addNotification({
        id: Date.now(),
        message: "User status updated successfully!",
        status: true,
        archive: false,
      });

      await callListApi();
    } catch (error) {
      console.error("Error occurred:", error);
      addNotification({
        id: Date.now(),
        message: "Something went wrong while updating the user status.",
        status: false,
        archive: false,
      });
    } finally {
      setLoading(false);
      setShow(false);
    }
  };

  const Confirmation = ({ userId, status, setShow }) => {
    return (
      <>
        <div className="pos-rel" style={{ width: "480px", padding: "40px" }}>
          <CiCircleRemove
            className="cursor-pointer transition hover-color-primary"
            onClick={() => setShow(false)}
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              fontSize: "32px",
            }}
          />
          <p className="color-tertiary">
            Are you sure You want to{" "}
            <strong>{status === 1 ? "deactivate" : "activate"}</strong> the
            user?
          </p>
          <div
            className="container flex center-center"
            style={{
              gap: "20px",
              padding: "12px",
            }}
          >
            <ButtonTextIcon
              title="Confirm"
              disabled={loading}
              icon={!loading ? <AiOutlineCheckCircle /> : <LoaderIcon />}
              onClick={() => handleConfirm(userId, status, setShow)}
            />
            <ButtonLightTextIcon
              title="Cancel"
              icon={<FaArrowLeftLong />}
              onClick={() => setShow(false)}
            />
          </div>
        </div>
      </>
    );
  };

  const handleDownload = useCallback(async () => {
    try {
      const searchTypeMap = {
        name: 1,
        email: 2,
        accountNumber: 3,
        balance: 4,
        date: 5,
        status: 6,
      };

      const {
        filterType,
        search,
        minBalance,
        maxBalance,
        startDate,
        endDate,
        statusID,
      } = initialValues;

      const searchType = searchTypeMap[filterType] || "";

      const downloadParams = buildQueryParams({
        search: search || "",
        searchType: searchType,
        minBalance: minBalance || "",
        maxBalance: maxBalance || "",
        startDate: startDate || "",
        endDate: endDate || "",
        statusID: statusID || "",
      });

      const endPoint = `/api/1.0.0/user/customer`;

      const response = await fetch(
        `${API_URL}${endPoint}/download?${downloadParams}`,
        {
          method: "GET",
          headers: {
            "X-JWT-Assertion": localStorage.getItem("token"),
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download file");
      }

      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute("download", "CustomerData.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error during file download:", error);
    }
  }, [initialValues]);

  useEffect(() => {
    callListApi();
    setSegmentTitle("Customers");
  }, [location.search]);

  useEffect(() => {
    setInitialValues({
      filterType: filterType || "",
      search: filterType === "balance" ? "" : search || "",
      minBalance: filterType === "balance" ? minBalance || "" : "",
      maxBalance: filterType === "balance" ? maxBalance || "" : "",
      startDate: filterType === "date" && startDate ? startDate : "",
      endDate: filterType === "date" && endDate ? endDate : "",
      statusID: filterType === "status" ? statusID || "" : "",
    });
  }, [location.search]);

  const FormValidation = Yup.object().shape({
    startDate: Yup.date().nullable().typeError("Invalid Start Date"),
    endDate: Yup.date()
      .nullable()
      .typeError("Invalid End Date")
      .test(
        "endDateCheck",
        "End Date cannot be earlier than Start Date",
        function (value) {
          const { startDate } = this.parent;
          if (value && startDate) {
            return value >= startDate;
          }
          return true;
        }
      ),
    filterType: Yup.string(),
    minBalance: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue !== undefined && originalValue !== null
          ? String(originalValue).trim() === ""
            ? null
            : value
          : null
      )
      .min(0, "Min Balance cannot be negative")
      .test(
        "is-required-when-balance",
        "Min Balance is required when Filter Type is Balance",
        function (value) {
          const { filterType } = this.parent;
          return filterType !== "balance" || value !== null;
        }
      ),
    maxBalance: Yup.number()
      .nullable()
      .transform((value, originalValue) => {
        if (
          originalValue === undefined ||
          originalValue === null ||
          String(originalValue).trim() === ""
        ) {
          return null;
        }
        return value;
      })
      .min(0, "Max Balance cannot be negative")
      .test(
        "is-greater-or-equal",
        "Max Balance must be greater than or equal to Min Balance",
        function (value) {
          const { minBalance, filterType } = this.parent;
          return (
            filterType !== "balance" ||
            value === null ||
            minBalance === null ||
            value >= minBalance
          );
        }
      ),
  });

  const toggleTxnId = (d) => {
    if (allChecked) {
      setExcludedTxnIds((prevIds) => {
        if (prevIds.includes(d.id)) {
          return prevIds.filter((id) => id !== d.id);
        } else {
          return [...prevIds, d.id];
        }
      });
    } else {
      setIncludedTxnIds((prevIds) => {
        if (prevIds.includes(d.id)) {
          return prevIds.filter((id) => id !== d.id);
        } else {
          return [...prevIds, d.id];
        }
      });
    }
  };

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setAllChecked(isChecked);
    setIncludedTxnIds([]);
    setExcludedTxnIds([]);
  };

  return (
    <>
      <PageTitle title="Customers" />
      <FullPageLoader isActive={loading} />
      <section>
        <div className="flex center-left responsive-container">
          <div>
            <Formik
              initialValues={initialValues}
              validationSchema={FormValidation}
              enableReinitialize={true}
              onSubmit={async (values) => {
                const queryParams = buildQueryParams({
                  filterType: values.filterType,
                  search: values.search,
                  pageNo: 0,
                  pageSize,
                  minBalance: values.minBalance,
                  maxBalance: values.maxBalance,
                  startDate: values.startDate,
                  endDate: values.endDate,
                  statusID: values.statusID,
                });
                navigate(`/customers?${queryParams}`);
                setAllChecked(false);
                setIncludedTxnIds([]);
                setExcludedTxnIds([]);
              }}
            >
              {({ values, errors, touched, setFieldValue, resetForm }) => (
                <Form>
                  <div
                    className="flex center-left responsive-container"
                    style={{
                      gap: "12px",
                      marginBottom: "-20px",
                      marginLeft: "-18px",
                    }}
                  >
                    <div
                      className={`responsive-select `}
                      style={{
                        width: "180px",
                      }}
                    >
                      <ResultsPerPage
                        targetURL={`/customers?${buildQueryParams({
                          filterType,
                          search,
                          minBalance,
                          maxBalance,
                          startDate,
                          endDate,
                          statusID: statusID || "",
                        })}&pageNo=0&pageSize=`}
                      />
                      <p>&nbsp;</p>
                    </div>
                    <div>
                      <Field
                        style={{
                          width: "170px",
                        }}
                        as="select"
                        name="filterType"
                        className={`responsive-select ${
                          errors.filterType && touched.filterType ? "error" : ""
                        }`}
                        onChange={(event) => {
                          setFieldValue("filterType", event.target.value);
                          setFieldValue("search", "");
                          setFieldValue("minBalance", "");
                          setFieldValue("maxBalance", "");
                          if (event.target.value === "date") {
                            setFieldValue("startDate", "");
                            setFieldValue("endDate", "");
                          }
                        }}
                      >
                        <option value="">Select Filter</option>
                        <option value="name">Name</option>
                        <option value="email">Email</option>
                        <option value="accountNumber">Account Number</option>
                        <option value="balance">Balance</option>
                        <option value="date">Date</option>
                        <option value="status">Status</option>
                      </Field>
                      <p>&nbsp;</p>
                    </div>
                    {values.filterType &&
                      (values.filterType === "balance" ? (
                        <div
                          className="balance-filter flex "
                          style={{
                            gap: "12px",
                            width: "300px",
                          }}
                        >
                          <div
                            style={{
                              margin: "-2px",
                            }}
                          >
                            <Field
                              type="number"
                              name="minBalance"
                              placeholder="Min Balance"
                              className={`col-1-1 ${
                                errors.minBalance && touched.minBalance
                                  ? "error"
                                  : ""
                              }`}
                            />
                            <p style={{ color: "red", fontSize: "12px" }}>
                              {errors.minBalance && touched.minBalance ? (
                                errors.minBalance
                              ) : (
                                <p>&nbsp;</p>
                              )}
                            </p>
                          </div>
                          <div
                            style={{
                              margin: "-2px",
                            }}
                          >
                            <Field
                              type="number"
                              name="maxBalance"
                              placeholder="Max Balance"
                              className={`col-1-1 ${
                                errors.maxBalance && touched.maxBalance
                                  ? "error"
                                  : ""
                              }`}
                            />
                            <p style={{ color: "red", fontSize: "12px" }}>
                              {errors.maxBalance && touched.maxBalance ? (
                                errors.maxBalance
                              ) : (
                                <p>&nbsp;</p>
                              )}
                            </p>
                          </div>
                        </div>
                      ) : values.filterType === "status" ? (
                        <div
                          style={{
                            marginBottom: "23px",
                          }}
                        >
                          <Field
                            style={{ width: "120px" }}
                            className="responsive-select"
                            as="select"
                            name="status"
                            onChange={(event) => {
                              const { value } = event.target;

                              setFieldValue("status", value);

                              if (value === "active") {
                                setFieldValue("statusID", 1);
                              } else if (value === "inActive") {
                                setFieldValue("statusID", 2);
                              } else if (value === "") {
                                setFieldValue("statusID", "");
                              }
                            }}
                          >
                            <option value="">All</option>
                            <option value="active">Active</option>
                            <option value="inActive">Inactive</option>
                          </Field>
                        </div>
                      ) : values.filterType === "date" ? (
                        <div
                          className="date-filter flex"
                          style={{ gap: "12px" }}
                        >
                          <div style={{ margin: "-2px" }}>
                            <Field
                              type="date"
                              name="startDate"
                              className={`col-1-1 ${
                                errors.startDate && touched.startDate
                                  ? "error"
                                  : ""
                              }`}
                            />
                            <p style={{ color: "red", fontSize: "12px" }}>
                              {errors.startDate && touched.startDate ? (
                                errors.startDate
                              ) : (
                                <p>&nbsp;</p>
                              )}
                            </p>
                          </div>
                          <div style={{ margin: "-2px" }}>
                            <Field
                              type="date"
                              name="endDate"
                              className={`col-1-1 ${
                                errors.endDate && touched.endDate ? "error" : ""
                              }`}
                            />
                            <p style={{ color: "red", fontSize: "12px" }}>
                              {errors.endDate && touched.endDate ? (
                                errors.endDate
                              ) : (
                                <p>&nbsp;</p>
                              )}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="balance-filter" style={{ gap: "12px" }}>
                          <Field
                            type="text"
                            name="search"
                            placeholder={`Search by ${
                              values.filterType || "..."
                            }`}
                            className={`col-1-1 ${
                              errors.search && touched.search ? "error" : ""
                            }`}
                          />
                          <p>&nbsp;</p>
                        </div>
                      ))}
                    <div className="flex " style={{ gap: "12px" }}>
                      <div>
                        <ButtonTextIcon
                          title="Search"
                          type="submit"
                          icon={<CiSearch />}
                          disabled={
                            (!values.search &&
                              values.filterType !== "balance" &&
                              values.filterType !== "date" &&
                              values.filterType !== "status") ||
                            (values.filterType === "balance" &&
                              values.minBalance === "" &&
                              values.maxBalance === "") ||
                            (values.filterType === "date" &&
                              !values.startDate &&
                              !values.endDate)
                          }
                        />
                        <p>&nbsp;</p>
                      </div>

                      <div className="responsive-button">
                        <ButtonTextIcon
                          title="Reset"
                          type="button"
                          onClick={() => {
                            resetForm();
                            resetFilters();
                          }}
                          icon={<CiRedo />}
                        />
                        <p>&nbsp;</p>
                      </div>
                      <div className="responsive-button">
                        <NavLink
                          to={`/customers/notification?tab=app`}
                          state={{
                            ...{ allChecked, includedTxnIds, excludedTxnIds },

                            ...(values.filterType === "name" && {
                              filterType: 1,
                              search: values.search,
                            }),
                            ...(values.filterType === "email" && {
                              filterType: 2,
                              search: values.search,
                            }),
                            ...(values.filterType === "accountNumber" && {
                              filterType: 3,
                              search: values.search,
                            }),
                            ...(values.filterType === "balance" && {
                              filterType: 4,
                              minBalance: values.minBalance,
                              maxBalance: values.maxBalance,
                            }),
                            ...(values.filterType === "date" && {
                              filterType: 5,
                              startDate: values.startDate,
                              endDate: values.endDate,
                            }),
                            ...(values.filterType === "status" && {
                              filterType: 6,
                              statusID: values.statusID,
                            }),
                          }}
                        >
                          <ButtonTextIcon
                            type="button"
                            title={"Notify"}
                            disabled={
                              statusID === "2" ||
                              (includedTxnIds.length === 0 &&
                                excludedTxnIds.length === 0 &&
                                !allChecked) ||
                              !(userRole === 9 || userRole === 1) // Disable if userRole is not 9 or 1
                            }
                            icon={<IoIosMegaphone />}
                          />
                        </NavLink>

                        <p>&nbsp;</p>
                      </div>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
          {userRole === 1 && (
            <div className="responsive-button">
              <ButtonTextIcon
                onClick={handleDownload}
                type="button"
                title={"Download"}
                icon={<CiInboxIn />}
              />
            </div>
          )}
        </div>

        <div className="results">
          <div className="rs-table-holder">
            <table className="rs-table">
              <thead>
                <tr>
                  <th>
                    {data?.profileResponseDTOS !== null ? (
                      <div className="flex center-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={allChecked}
                          onChange={handleSelectAll}
                        />
                      </div>
                    ) : (
                      "#"
                    )}
                  </th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Acc. No.</th>
                  <th>Balance</th>

                  <th>Active/Inactive</th>
                  <th>Registered On</th>
                </tr>
              </thead>
              {data && (
                <tbody>
                  {data.profileResponseDTOS?.map((d, i) => (
                    <tr key={i}>
                      <td>
                        <div className="flex center-center">
                          <input
                            type="checkbox"
                            checked={
                              allChecked
                                ? !excludedTxnIds.includes(d.id)
                                : includedTxnIds.includes(d.id)
                            }
                            onChange={() => toggleTxnId(d)}
                            value={d.id}
                          />
                        </div>
                      </td>
                      <td>{d.fullName ? d.fullName : "-"}</td>
                      <td>{d?.email || "-"}</td>
                      <td>{d?.walletAccountDetails?.accountNumber || "-"}</td>
                      <td>
                        {APP_CURRENCY}{" "}
                        {parseInt(d.walletAccountDetails?.balance || 0).toFixed(
                          2
                        )}
                        /-
                      </td>
                      <td>
                        <div className="flex center-center">
                          <RSModal
                            selector={
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent:
                                    d.statusID === 1
                                      ? "flex-end"
                                      : "flex-start",
                                  backgroundColor:
                                    d.statusID === 1 ? "#0073ba" : "#AFAFAA",
                                  width: "45px",
                                  height: "22px",
                                  borderRadius: "15px",
                                  padding: "5px",
                                  margin: "0 5px 0 5px",
                                  cursor: "pointer",
                                  transition:
                                    "background-color 0.3s ease, justify-content 0.3s ease",
                                }}
                              >
                                <div
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    backgroundColor: "white",
                                    borderRadius: "50%",
                                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                                    transition: "transform 0.3s ease",
                                  }}
                                />
                              </div>
                            }
                            content={
                              <Confirmation userId={d.id} status={d.statusID} />
                            }
                          />
                        </div>
                      </td>
                      <td>
                        {d?.createdAt ? formatDateTime(d.createdAt) : "-"}
                      </td>
                    </tr>
                  ))}
                  {(!data ||
                    !data.profileResponseDTOS ||
                    data.profileResponseDTOS.length === 0) &&
                    !loading && (
                      <tr>
                        <td
                          colSpan={7}
                          style={{ textAlign: "center", padding: "10px" }}
                        >
                          <p> No records found. </p>
                        </td>
                      </tr>
                    )}
                </tbody>
              )}
            </table>
          </div>
        </div>
        <br />
        {data && (
          <Pagination
            totalRecords={data.totalRecords}
            pageNo={data.pageNo}
            pageSize={data.pageSize}
            baseUrl={`/customers?${buildQueryParams({
              filterType,
              search,
              minBalance,
              maxBalance,
              startDate,
              endDate,
              statusID,
            })}&`}
          />
        )}
      </section>
    </>
  );
};

export default CustomerList;
